// Field Rules Engine for checkout fields
// Replicates logic from old app's field-rules-engine.js

import { useMemo, useCallback } from 'react';
import { useOrderStore } from '@/stores/orderStore';
import type { AppQuestion } from '@/types';

export interface RuleResult {
  skip: boolean;       // Skip rendering entirely (field not applicable)
  hide: boolean;       // Render but keep hidden (auto-fill scenarios)
  defaultValue: string | null;  // Pre-populate this value
  skipValidation: boolean;      // Don't validate this field
  forceRequired: boolean;       // Force field to be required regardless of API config
}

type PropertyType = 'Residential' | 'Business';
type RentOrOwn = 'Rent' | 'Own';

interface FieldRuleContext {
  propertyType: PropertyType | null;
  rentOrOwn: RentOrOwn | null;
}

interface FieldRule {
  ruleId: string;
  match: {
    fieldNamePattern?: RegExp;
    fieldNames?: string[];
  };
  conditions?: {
    propertyType?: PropertyType;
  };
  actions: {
    skip?: boolean;
    hide?: boolean;
    defaultValue?: string | ((ctx: FieldRuleContext) => string);
    skipValidation?: boolean;
    forceRequired?: boolean;
  };
}

// Business-specific field names that should be hidden for residential customers
const BUSINESS_FIELD_NAMES = [
  'business telephone',
  'business name',
  'business tax id',
  'type of business',
  'business type',
  'federal tax id',
  'tax id',
];

// Business fields that must always be required when property type is Business
const BUSINESS_REQUIRED_FIELD_NAMES = ['business tax id', 'business telephone'];

// Residential-specific field names that should be hidden for business customers
const RESIDENTIAL_FIELD_NAMES = [
  'home telephone',
  'renter insurance',
  'renters insurance',
  'number of occupants',
  'occupants',
  'pet information',
  'pets',
  'do you have pets',
];

// Define all field rules
const FIELD_RULES: FieldRule[] = [
  // Rule 1: Auto-select "Keep my information confidential" - hide and auto-set
  {
    ruleId: 'auto-select-confidential',
    match: {
      fieldNamePattern: /keep.*information.*confidential/i,
    },
    actions: {
      hide: true,
      defaultValue: 'Yes',
      skipValidation: true,
    },
  },

  // Rule 2: Hide business fields for residential customers
  {
    ruleId: 'hide-business-fields-residential',
    match: {
      fieldNames: BUSINESS_FIELD_NAMES,
    },
    conditions: {
      propertyType: 'Residential',
    },
    actions: {
      skip: true,
    },
  },

  // Rule 3: Hide residential fields for business customers
  {
    ruleId: 'hide-residential-fields-business',
    match: {
      fieldNames: RESIDENTIAL_FIELD_NAMES,
    },
    conditions: {
      propertyType: 'Business',
    },
    actions: {
      skip: true,
    },
  },

  // Rule 4: Auto-populate "Do you own or rent" from personal info
  {
    ruleId: 'auto-populate-rent-own',
    match: {
      fieldNamePattern: /do\s+you\s+(own\s+or\s+rent|rent\s+or\s+own)/i,
    },
    actions: {
      defaultValue: (ctx) => ctx.rentOrOwn || '',
    },
  },

  // Rule 5: Force-require business tax ID and telephone for business customers
  {
    ruleId: 'force-require-business-fields',
    match: {
      fieldNames: BUSINESS_REQUIRED_FIELD_NAMES,
    },
    conditions: {
      propertyType: 'Business',
    },
    actions: {
      forceRequired: true,
    },
  },

  // Rule 6: Hide "Employer Name" for business customers
  {
    ruleId: 'hide-employer-name-business',
    match: {
      fieldNamePattern: /^employer\s*name$/i,
    },
    conditions: {
      propertyType: 'Business',
    },
    actions: {
      skip: true,
    },
  },
];

/**
 * Check if a field name matches a rule's match criteria
 */
function matchesRule(fieldName: string, rule: FieldRule): boolean {
  const normalizedName = fieldName.toLowerCase().trim();

  // Check pattern match
  if (rule.match.fieldNamePattern) {
    if (rule.match.fieldNamePattern.test(normalizedName)) {
      return true;
    }
  }

  // Check exact/partial name matches
  if (rule.match.fieldNames) {
    for (const matchName of rule.match.fieldNames) {
      if (normalizedName.includes(matchName.toLowerCase())) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Check if rule conditions are met
 */
function conditionsMet(rule: FieldRule, context: FieldRuleContext): boolean {
  if (!rule.conditions) {
    return true; // No conditions = always apply
  }

  if (rule.conditions.propertyType) {
    if (context.propertyType !== rule.conditions.propertyType) {
      return false;
    }
  }

  return true;
}

/**
 * Hook that provides field evaluation based on rules engine
 */
export function useFieldRules() {
  const personalInfo = useOrderStore((state) => state.personalInfo);

  // Build context from store
  const context = useMemo<FieldRuleContext>(() => ({
    propertyType: personalInfo?.propertyType ?? null,
    rentOrOwn: personalInfo?.rentOrOwn ?? null,
  }), [personalInfo?.propertyType, personalInfo?.rentOrOwn]);

  /**
   * Evaluate a field against all rules and return the combined result
   */
  const evaluateField = useCallback((question: AppQuestion): RuleResult => {
    const result: RuleResult = {
      skip: false,
      hide: false,
      defaultValue: null,
      skipValidation: false,
      forceRequired: false,
    };

    for (const rule of FIELD_RULES) {
      // Check if field matches this rule
      if (!matchesRule(question.Name, rule)) {
        continue;
      }

      // Check if conditions are met
      if (!conditionsMet(rule, context)) {
        continue;
      }

      // Apply actions
      if (rule.actions.skip) {
        result.skip = true;
      }
      if (rule.actions.hide) {
        result.hide = true;
      }
      if (rule.actions.skipValidation) {
        result.skipValidation = true;
      }
      if (rule.actions.forceRequired) {
        result.forceRequired = true;
      }
      if (rule.actions.defaultValue !== undefined) {
        if (typeof rule.actions.defaultValue === 'function') {
          result.defaultValue = rule.actions.defaultValue(context);
        } else {
          result.defaultValue = rule.actions.defaultValue;
        }
      }
    }

    return result;
  }, [context]);

  return { evaluateField, context };
}
